import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react";
import { useId } from "react";

import { cn } from "@/lib/cn";

const CONTROL =
  "h-touch-lg w-full rounded-control border border-line bg-surface px-3 text-sm text-ink-900 " +
  "outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 " +
  "disabled:bg-canvas disabled:text-ink-400";

interface FieldShellProps {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  children: (id: string) => ReactNode;
  className?: string | undefined;
}

/**
 * Label, control, hint and error as one unit.
 *
 * `useId` rather than a caller-supplied id: a settings form has a dozen fields
 * and the same component may be rendered twice on a page, so hand-written ids
 * eventually collide and quietly break the label association — which is
 * invisible to everyone except the person using a screen reader.
 */
function FieldShell({ label, hint, error, children, className }: FieldShellProps) {
  const id = useId();

  return (
    <div className={cn("min-w-0", className)}>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-ink-900">
        {label}
      </label>
      {children(id)}
      {error ? (
        <p className="mt-1.5 text-xs text-danger-700" role="alert">
          {error}
        </p>
      ) : (
        hint && <p className="mt-1.5 text-xs text-ink-500">{hint}</p>
      )}
    </div>
  );
}

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "className"> {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  className?: string | undefined;
}

export function TextField({ label, hint, error, className, ...props }: TextFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} className={className}>
      {(id) => (
        <input
          id={id}
          className={cn(CONTROL, error && "border-danger-500")}
          aria-invalid={error ? true : undefined}
          {...props}
        />
      )}
    </FieldShell>
  );
}

export interface SelectFieldProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "id" | "className"> {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  options: { value: string; label: string }[];
  className?: string | undefined;
}

export function SelectField({
  label,
  hint,
  error,
  options,
  className,
  ...props
}: SelectFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} className={className}>
      {(id) => (
        <select id={id} className={cn(CONTROL, "pr-8")} {...props}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  );
}

export interface ToggleFieldProps {
  label: string;
  hint?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

/** A switch with its explanation beside it, for the many boolean settings. */
export function ToggleField({ label, hint, checked, onChange, disabled }: ToggleFieldProps) {
  const id = useId();

  // `min-w-0` on the root, not only on the label block. A flex or grid item
  // defaults to `min-width: auto`, which is its content's intrinsic width — so
  // two of these side by side in a grid, with a long hint, silently overflow
  // their tracks and the switch of one lands on top of the label of the next.
  return (
    <div className="flex min-w-0 items-start justify-between gap-4 py-3">
      <div className="min-w-0">
        <label htmlFor={id} className="block text-sm font-medium text-ink-900">
          {label}
        </label>
        {hint && <p className="mt-0.5 text-xs leading-relaxed text-ink-500">{hint}</p>}
      </div>

      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative mt-0.5 h-6 w-11 shrink-0 rounded-pill transition-colors disabled:opacity-50",
          checked ? "bg-brand-500" : "bg-line",
        )}
      >
        {/* `left-0.5` matters. Without it the knob is `left: auto`, so it sits at
            its *static* position — which a button centres — and the translate
            then pushes it clean out of the 44px track: measured 20px past the
            right edge, where it landed on top of the next grid column's label.
            Anchored to the left edge, the two offsets are 2px and 22px, leaving
            a symmetric 2px inset at either end. */}
        <span
          className={cn(
            "absolute left-0.5 top-0.5 size-5 rounded-full bg-white shadow-card",
            "transition-transform",
            checked ? "translate-x-5" : "translate-x-0",
          )}
        />
      </button>
    </div>
  );
}
